import {
    BACKTEST_PERIODS,
    type BacktestPeriod,
    type StrategyVariantPeriodSummary,
    type StrategyVariantSavedCohort
} from '@einhaus/project314-types';
import { parseSavedCohortInput } from './savedCohortInput';
import { hasIncompleteSavedCohorts } from './savedCohortCoverage';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { inferRouterOutputs } from '@trpc/server';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { FaExternalLinkAlt, FaSyncAlt } from 'react-icons/fa';
import UserLayout from '@/components/Layout/UserLayout';
import { Alert, Button, Card, DataTable, FormSelect } from '@/components/UI';
import StatusBadge from '@/components/UI/StatusBadge';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc, trpcClient, trpcPostUnbatched } from '@/api/trpc';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type ListFamiliesOutput = RouterOutputs['strategyVariant']['listFamilies'];
type CompareFamilyOutput = RouterOutputs['strategyVariantComparison']['compareFamily'];
type ComparisonVariant = CompareFamilyOutput['variants'][number];
type VariantMetrics = ComparisonVariant['metrics'];
type ComparisonMode = CompareFamilyOutput['mode'];
type RankBy = CompareFamilyOutput['rankBy'];
type OptionStrategy = CompareFamilyOutput['availableOptionStrategies'][number];
type PeriodCutoff = CompareFamilyOutput['periodCutoff'];

type FamilySummary = ListFamiliesOutput['families'][number];
type Scorecard = RouterOutputs['scorecards']['list'][number];
type SelectOption = { disabled?: boolean; label: string; value: number };

// Each row is a real variant strategy. To show *what differs* between rows (not just the metrics), fetch each
// variant's actual attached screener + scorecard — the same data the family view shows.
interface VariantArtifacts {
    activeScorecard: Scorecard | null;
    activeScreenerRuleId: number | null;
}

const fetchComparisonArtifacts = async (strategyIds: number[]): Promise<Map<number, VariantArtifacts>> => {
    const [scorecards, strategiesResult] = await Promise.all([
        trpcClient.scorecards.list.query(),
        trpcClient.strategy.fetchStrategies.query({ requireActive: false, ids: strategyIds })
    ]);

    const byStrategyId = new Map<number, VariantArtifacts>();
    const scorecardById = new Map(scorecards.map((scorecard) => [scorecard.id, scorecard]));
    const strategyById = new Map(strategiesResult.strategies.map((strategy) => [strategy.id, strategy]));

    strategyIds.forEach((strategyId) => {
        const strategy = strategyById.get(strategyId);
        const activeScorecardId = strategy?.activeScorecardId ?? null;

        byStrategyId.set(strategyId, {
            activeScorecard: activeScorecardId === null ? null : (scorecardById.get(activeScorecardId) ?? null),
            activeScreenerRuleId: strategy?.activeScreenerRuleId ?? null
        });
    });

    return byStrategyId;
};

const toPositiveId = (value: string | null): number => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

interface BestMetrics {
    comparableCount: number;
    bestReturn: number | null;
    bestSharpe: number | null;
    bestProfitFactor: number | null;
    bestDrawdown: number | null;
    bestWinRate: number | null;
    bestReturnOnRiskCapital: number | null;
}

const maxOrNull = (values: number[]): number | null => (values.length ? Math.max(...values) : null);

const numericValues = (metrics: VariantMetrics[], select: (metric: VariantMetrics) => number | null): number[] =>
    metrics.map(select).filter((value): value is number => value !== null);

// Best value per metric across variations that actually have backtest results, so the winning cell can be
// highlighted. Drawdown is stored as a negative number (a loss), so the best drawdown is the one closest to
// zero (the maximum). Return/Sharpe/profit factor/win rate are best-when-highest.
const computeBestMetrics = (metrics: VariantMetrics[]): BestMetrics => {
    const eligible = metrics.filter((metric) => metric.completedBacktestCount > 0);

    return {
        comparableCount: eligible.length,
        bestReturn: maxOrNull(numericValues(eligible, (metric) => metric.avgReturnPercent)),
        bestSharpe: maxOrNull(numericValues(eligible, (metric) => metric.avgSharpeRatio)),
        bestProfitFactor: maxOrNull(numericValues(eligible, (metric) => metric.profitFactor)),
        bestDrawdown: maxOrNull(numericValues(eligible, (metric) => metric.avgMaxDrawdownPercent)),
        bestWinRate: maxOrNull(numericValues(eligible, (metric) => metric.avgWinRate)),
        bestReturnOnRiskCapital: maxOrNull(numericValues(eligible, (metric) => metric.avgReturnOnRiskCapitalPercent))
    };
};

const isMetricWinner = (rankable: boolean, best: number | null, value: number | null): boolean =>
    rankable && best !== null && value === best;

// A stable color per variation, shown as a dot beside each row so variations are easy to track across the table.
const VARIANT_SERIES_COLORS = ['#60a5fa', '#34d399', '#fbbf24', '#f472b6', '#a78bfa', '#22d3ee', '#fb923c', '#a3e635'] as const;
const BASE_SERIES_COLOR = '#a1a1aa';

const buildFamilyOptions = (families: FamilySummary[]): SelectOption[] =>
    families.map((summary) => ({
        label: `${summary.family.name} (${summary.variantCount})`,
        value: summary.family.id
    }));

const resolveSelectedFamilyId = (requestedFamilyId: number, families: FamilySummary[]): number =>
    requestedFamilyId > 0 ? requestedFamilyId : (families[0]?.family.id ?? 0);

const isInitialFamiliesLoad = (isLoading: boolean, data: ListFamiliesOutput | undefined): boolean => isLoading && data === undefined;

const StrategyVariantComparisons: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [requestedFamilyId, setRequestedFamilyId] = useState(() => toPositiveId(searchParams.get('familyId')));
    const [cohortText, setCohortText] = useState('');
    const [savedCohorts, setSavedCohorts] = useState<StrategyVariantSavedCohort[] | undefined>();
    const [cohortError, setCohortError] = useState<string | null>(null);
    const [reportingCutoffDate, setReportingCutoffDate] = useState('');
    const [reportingCutoffDateTime, setReportingCutoffDateTime] = useState<string | undefined>();
    const [mode, setMode] = useState<ComparisonMode>('best');
    const [rankBy, setRankBy] = useState<RankBy>('returnOnRiskCapital');
    const [optionBuyingStrategyId, setOptionBuyingStrategyId] = useState<number | null>(null);

    const familiesQuery = useQuery({
        ...trpc.strategyVariant.listFamilies.queryOptions(),
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const families = useMemo(() => familiesQuery.data?.families ?? [], [familiesQuery.data]);
    const familyOptions = useMemo(() => buildFamilyOptions(families), [families]);
    const selectedFamilyId = resolveSelectedFamilyId(requestedFamilyId, families);

    const comparisonQuery = useQuery({
        ...trpcPostUnbatched.strategyVariantComparison.compareFamily.queryOptions({
            familyId: selectedFamilyId,
            mode,
            rankBy,
            optionBuyingStrategyId,
            ...(savedCohorts ? { savedCohorts } : {}),
            ...(reportingCutoffDateTime ? { reportingCutoffDateTime } : {})
        }),
        enabled: selectedFamilyId > 0,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const availableOptionStrategies = useMemo(() => comparisonQuery.data?.availableOptionStrategies ?? [], [comparisonQuery.data]);

    const changeFamily = (familyId: number) => {
        setRequestedFamilyId(familyId);
        setOptionBuyingStrategyId(null);
        setSavedCohorts(undefined);
        setCohortText('');
        setReportingCutoffDate('');
        setReportingCutoffDateTime(undefined);
        setCohortError(null);
    };

    useEffect(() => {
        setSearchParams(selectedFamilyId > 0 ? { familyId: String(selectedFamilyId) } : {}, { replace: true });
    }, [selectedFamilyId, setSearchParams]);

    const variants = useMemo(() => comparisonQuery.data?.variants ?? [], [comparisonQuery.data]);
    const baseStrategyId = comparisonQuery.data?.baseStrategyId ?? null;

    const variantStrategyIds = useMemo(
        () => Array.from(new Set(variants.map((variant) => variant.strategyId))).sort((left, right) => left - right),
        [variants]
    );

    const artifactsQuery = useQuery({
        queryKey: ['comparison-variant-artifacts', selectedFamilyId, variantStrategyIds] as const,
        queryFn: () => fetchComparisonArtifacts(variantStrategyIds),
        enabled: variantStrategyIds.length > 0,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    const artifactsByStrategyId = useMemo(() => artifactsQuery.data ?? new Map<number, VariantArtifacts>(), [artifactsQuery.data]);
    const isComparisonLoading = comparisonQuery.isLoading && selectedFamilyId > 0;

    if (isInitialFamiliesLoad(familiesQuery.isLoading, familiesQuery.data)) {
        return (
            <UserLayout title="Strategy Variant Comparisons - Project 314">
                <DataLoadingSpinner text="Loading strategy families..." />
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1600px" title="Strategy Variant Comparisons - Project 314">
            <div className="container mx-auto px-4 py-4">
                <ComparisonHeader
                    familyCount={families.length}
                    familyOptions={familyOptions}
                    selectedFamilyId={selectedFamilyId}
                    onFamilyChange={changeFamily}
                />
                <Card className="mb-3">
                    <label className="block text-sm font-medium" htmlFor="saved-backtest-cohort">
                        Saved backtest jobs
                    </label>
                    <p className="my-2 text-xs text-zinc-400">
                        Freeze a comparison using one strategy per line: strategy ID: job IDs or ranges. For example, 123: 1001-1005.
                        Include empty and pending jobs to preserve the evaluated population.
                    </p>
                    <textarea
                        id="saved-backtest-cohort"
                        className="w-full rounded border border-zinc-700 bg-zinc-900 p-2 text-sm"
                        rows={3}
                        value={cohortText}
                        onChange={(event) => setCohortText(event.target.value)}
                    />
                    <div className="mt-2 flex gap-2">
                        <Button
                            onClick={() => {
                                try {
                                    const parsed = parseSavedCohortInput(cohortText);
                                    setSavedCohorts(parsed);
                                    setReportingCutoffDateTime(undefined);
                                    if (parsed) {
                                        setMode('pooled');
                                        setOptionBuyingStrategyId(null);
                                    }
                                    setCohortError(null);
                                } catch (error) {
                                    setCohortError(error instanceof Error ? error.message : 'Invalid saved jobs');
                                }
                            }}
                        >
                            Apply saved jobs
                        </Button>
                        <Button
                            onClick={() => {
                                setSavedCohorts(undefined);
                                setReportingCutoffDateTime(undefined);
                                setCohortText('');
                                setCohortError(null);
                            }}
                        >
                            Clear saved jobs
                        </Button>
                    </div>
                    <div className="mt-4 flex flex-wrap items-end gap-2">
                        <label className="text-sm" htmlFor="reporting-cutoff-date">
                            Reporting cutoff (Eastern, midnight)
                            <input
                                id="reporting-cutoff-date"
                                type="date"
                                value={reportingCutoffDate}
                                onChange={(event) => setReportingCutoffDate(event.target.value)}
                                className="mt-1 block rounded border border-zinc-700 bg-zinc-900 p-2"
                            />
                        </label>
                        <Button
                            disabled={!savedCohorts || !reportingCutoffDate}
                            onClick={() => {
                                setMode('pooled');
                                setOptionBuyingStrategyId(null);
                                setReportingCutoffDateTime(`${reportingCutoffDate} 00:00:00`);
                            }}
                        >
                            Apply reporting cutoff
                        </Button>
                        <Button disabled={!reportingCutoffDateTime} onClick={() => setReportingCutoffDateTime(undefined)}>
                            Use saved cutoffs
                        </Button>
                    </div>
                    <p className="mt-2 text-xs text-zinc-400">
                        A custom cutoff reads existing trades from at most 2,000 exact saved jobs sharing one date window. It does not
                        change saved summaries, retrain rules or make previously viewed history fresh evidence.
                    </p>
                    {savedCohorts && (
                        <p className="mt-2 text-xs text-zinc-400">
                            Using exact saved jobs; configurations are not ranked over the full period. No new jobs are submitted.
                        </p>
                    )}
                </Card>
                {(cohortError || comparisonQuery.error) && (
                    <Alert className="mb-3" variant="warning">
                        {cohortError ?? comparisonQuery.error?.message}
                    </Alert>
                )}
                {comparisonQuery.data?.savedCohorts.map((cohort) => (
                    <Alert key={cohort.strategyId} className="mb-2" variant={hasIncompleteSavedCohorts([cohort]) ? 'warning' : 'info'}>
                        Strategy {cohort.strategyId}: {cohort.resultBacktestIds.length} results, {cohort.completedEmptyJobIds.length}{' '}
                        completed empty, {cohort.pendingJobIds.length} pending, {cohort.failedJobIds.length} failed/cancelled;{' '}
                        {cohort.startDateTime}–{cohort.endDateTime}. Exit {cohort.exitCandidateId ?? 'strategy default'}; option legs{' '}
                        {cohort.optionBuyingStrategyIds.filter((id) => id !== null).join(', ') || 'none'}; {cohort.restrictedJobCount}{' '}
                        restricted jobs.
                        {hasIncompleteSavedCohorts([cohort]) &&
                            ' Metrics cover completed results only. This comparison is incomplete; deltas and winner highlights are hidden.'}
                    </Alert>
                ))}
                {comparisonQuery.data?.populationAligned === false && (
                    <Alert className="mb-3" variant="warning">
                        Saved cohorts evaluate different entity populations. Their differences do not establish treatment lift.
                    </Alert>
                )}
                {comparisonQuery.data?.reportingCutoffDateTime && (
                    <Alert className="mb-3" variant="info">
                        Retrospective reporting cutoff: {comparisonQuery.data.reportingCutoffDateTime} Eastern. Trades stay with their entry
                        period, including actual exits across period boundaries. Saved research cutoffs and execution results are unchanged.
                    </Alert>
                )}
                {variants.some((variant) => variant.walkForwardYears?.length) && (
                    <Card className="mb-3 overflow-x-auto">
                        <h3 className="mb-2 font-semibold">Annual evaluation entry cohorts</h3>
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="p-2 text-left">Strategy</th>
                                    <th className="p-2 text-left">Entry dates</th>
                                    <th className="p-2">Trades</th>
                                    <th className="p-2">Mean return</th>
                                    <th className="p-2">Profit factor</th>
                                    <th className="p-2">Net profit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {variants.flatMap((variant) =>
                                    (variant.walkForwardYears ?? []).map((year) => (
                                        <tr key={`${variant.strategyId}-${year.yearNumber}`}>
                                            <td className="p-2">
                                                {variant.strategyId}: {variant.name}
                                            </td>
                                            <td className="p-2">
                                                {year.startDateTime.slice(0, 10)}–{year.endDateTime.slice(0, 10)}
                                            </td>
                                            <td className="p-2 text-center">{year.summary.metrics?.totalTrades ?? '—'}</td>
                                            <td className="p-2 text-center">
                                                {year.summary.metrics?.avgReturnPercent?.toFixed(2) ?? '—'}%
                                            </td>
                                            <td className="p-2 text-center">{year.summary.metrics?.profitFactor?.toFixed(2) ?? '—'}</td>
                                            <td className="p-2 text-center">${year.summary.metrics?.netProfit?.toFixed(2) ?? '—'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        <p className="mt-2 text-xs text-zinc-400">
                            Descriptive slices use the same final execution evidence; no annual refitting. Stock and option percentages use
                            different capital bases.
                        </p>
                    </Card>
                )}
                <ComparisonPanel
                    artifactsByStrategyId={artifactsByStrategyId}
                    availableOptionStrategies={availableOptionStrategies}
                    baseStrategyId={baseStrategyId}
                    familyName={comparisonQuery.data?.familyName ?? null}
                    hasFamily={selectedFamilyId > 0}
                    isLoading={isComparisonLoading}
                    mode={mode}
                    optionBuyingStrategyId={optionBuyingStrategyId}
                    cohortsComparable={
                        comparisonQuery.data?.populationAligned !== false &&
                        !hasIncompleteSavedCohorts(comparisonQuery.data?.savedCohorts ?? [])
                    }
                    periodCutoff={comparisonQuery.data?.periodCutoff ?? null}
                    rankBy={rankBy}
                    variants={variants}
                    onModeChange={(nextMode) => {
                        if (savedCohorts && nextMode === 'best') setCohortError('Clear saved jobs to use Best combination.');
                        else setMode(nextMode);
                    }}
                    onOpenStrategy={(strategyId) => navigate(`/user/tradingStrategy/${strategyId}`)}
                    onOptionStrategyChange={(id) => {
                        if (savedCohorts && id !== null)
                            setCohortError('Saved jobs already pin option legs. Clear saved jobs to use this filter.');
                        else setOptionBuyingStrategyId(id);
                    }}
                    onRankByChange={setRankBy}
                    onRefresh={() => comparisonQuery.refetch()}
                />
            </div>
        </UserLayout>
    );
};

interface ComparisonHeaderProps {
    familyCount: number;
    familyOptions: SelectOption[];
    selectedFamilyId: number;
    onFamilyChange: (familyId: number) => void;
}

const ComparisonHeader = (props: ComparisonHeaderProps) => {
    const familyOptions = props.familyOptions.length ? props.familyOptions : [{ label: 'No families', value: 0, disabled: true }];

    return (
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
                <h3 className="text-xl font-semibold text-white">Strategy Variant Comparisons</h3>
                <p className="mt-1 text-sm text-zinc-400">Compare every variation in a family using its existing backtests.</p>
            </div>
            <div className="flex items-end gap-2">
                <div className="w-64">
                    <FormSelect
                        disabled={props.familyCount === 0}
                        label="Strategy family"
                        options={familyOptions}
                        value={props.selectedFamilyId}
                        onChange={(event) => props.onFamilyChange(Number(event.target.value))}
                    />
                </div>
                <Button icon={<FaExternalLinkAlt />} size="sm" to="/user/tradingStrategies" variant="outline-dark">
                    Strategies
                </Button>
            </div>
        </div>
    );
};

interface ComparisonPanelProps {
    artifactsByStrategyId: Map<number, VariantArtifacts>;
    availableOptionStrategies: OptionStrategy[];
    baseStrategyId: number | null;
    familyName: string | null;
    hasFamily: boolean;
    isLoading: boolean;
    mode: ComparisonMode;
    optionBuyingStrategyId: number | null;
    periodCutoff: PeriodCutoff | null;
    cohortsComparable: boolean;
    rankBy: RankBy;
    variants: ComparisonVariant[];
    onModeChange: (mode: ComparisonMode) => void;
    onOpenStrategy: (strategyId: number) => void;
    onOptionStrategyChange: (optionBuyingStrategyId: number | null) => void;
    onRankByChange: (rankBy: RankBy) => void;
    onRefresh: () => void;
}

const MODE_OPTIONS: { label: string; value: ComparisonMode }[] = [
    { label: 'Best combination', value: 'best' },
    { label: 'All backtests', value: 'pooled' }
];

type ComparisonControlsProps = Pick<
    ComparisonPanelProps,
    | 'mode'
    | 'rankBy'
    | 'optionBuyingStrategyId'
    | 'availableOptionStrategies'
    | 'onModeChange'
    | 'onRankByChange'
    | 'onOptionStrategyChange'
> & { premiumMetricLabel: string };

const ComparisonControls = (props: ComparisonControlsProps) => {
    const rankByOptions: { label: string; value: RankBy }[] = [
        { label: 'Avg ROC', value: 'returnOnRiskCapital' },
        { label: props.premiumMetricLabel, value: 'return' },
        { label: 'Win rate', value: 'winRate' },
        { label: 'Profit factor', value: 'profitFactor' }
    ];

    return (
        <div className="mb-3 flex flex-col gap-2 border-b border-zinc-800 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex overflow-hidden rounded border border-zinc-700">
                    {MODE_OPTIONS.map((option) => (
                        <button
                            className={`px-3 py-1.5 text-xs ${
                                props.mode === option.value ? 'bg-blue-600 text-white' : 'bg-zinc-900 text-zinc-300 hover:bg-zinc-800'
                            }`}
                            key={option.value}
                            type="button"
                            onClick={() => props.onModeChange(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                {props.mode === 'best' && (
                    <label className="flex items-center gap-1.5 text-xs text-zinc-500">
                        Best by
                        <select
                            className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-200"
                            value={props.rankBy}
                            onChange={(event) => props.onRankByChange(event.target.value as RankBy)}
                        >
                            {rankByOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {props.availableOptionStrategies.length > 0 && (
                    <label className="flex items-center gap-1.5 text-xs text-zinc-500">
                        Option strategy
                        <select
                            className="max-w-[220px] rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-200"
                            value={props.optionBuyingStrategyId ?? ''}
                            onChange={(event) => props.onOptionStrategyChange(event.target.value ? Number(event.target.value) : null)}
                        >
                            <option value="">All option strategies</option>
                            {props.availableOptionStrategies.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
            </div>
            <p className="text-[11px] text-zinc-500">
                {props.mode === 'best'
                    ? 'Each variation shown with its strongest option × exit combination over the full period.'
                    : 'Each variation averaged across all of its backtests (blends every option × exit combination).'}
            </p>
        </div>
    );
};

const ComparisonPanel = (props: ComparisonPanelProps) => {
    const selectedOptionStrategy = props.optionBuyingStrategyId
        ? props.availableOptionStrategies.find((strategy) => strategy.id === props.optionBuyingStrategyId)
        : null;

    const includesSellToOpen =
        selectedOptionStrategy?.openTransactionType === 'sellToOpen' ||
        (!selectedOptionStrategy && props.availableOptionStrategies.some((strategy) => strategy.openTransactionType === 'sellToOpen')) ||
        props.variants.some((variant) => variant.bestCombination?.optionStrategyOpenTransactionTypes.includes('sellToOpen') ?? false);

    const premiumMetricLabel = includesSellToOpen ? 'Avg premium' : 'Avg trade';
    const refreshRequiredCount = props.variants.filter((variant) => variant.requiresBacktestRefresh).length;

    return (
        <Card
            responsivePadding
            headerActions={
                props.hasFamily ? (
                    <Button icon={<FaSyncAlt />} size="sm" variant="outline-dark" onClick={props.onRefresh}>
                        Refresh
                    </Button>
                ) : null
            }
            title={props.familyName ?? 'Comparison'}
        >
            {props.hasFamily && (
                <ComparisonControls
                    availableOptionStrategies={props.availableOptionStrategies}
                    mode={props.mode}
                    optionBuyingStrategyId={props.optionBuyingStrategyId}
                    premiumMetricLabel={premiumMetricLabel}
                    rankBy={props.rankBy}
                    onModeChange={props.onModeChange}
                    onOptionStrategyChange={props.onOptionStrategyChange}
                    onRankByChange={props.onRankByChange}
                />
            )}
            {props.isLoading ? (
                <DataLoadingSpinner text="Loading comparison..." />
            ) : props.variants.length === 0 ? (
                <EmptyComparison hasFamily={props.hasFamily} />
            ) : (
                <>
                    {refreshRequiredCount > 0 && (
                        <Alert className="mb-3" variant="warning">
                            {refreshRequiredCount === 1 ? 'One variation has' : `${refreshRequiredCount} variations have`} older backtests,
                            but none match the current strategy and execution version. Their metrics stay hidden until those backtests are
                            rerun.
                        </Alert>
                    )}
                    {props.periodCutoff && !props.periodCutoff.aligned && (
                        <Alert className="mb-3" variant="warning">
                            Training and walk-forward are split at different dates in this comparison (
                            {shortDate(props.periodCutoff.trainingEndDateTimeMin)} to {shortDate(props.periodCutoff.trainingEndDateTimeMax)}
                            ), so those periods cover different trades. Their differences from the base and best-value highlights are
                            hidden; overall results are unaffected.
                        </Alert>
                    )}
                    <ComparisonResults
                        artifactsByStrategyId={props.artifactsByStrategyId}
                        baseStrategyId={props.baseStrategyId}
                        cohortsComparable={props.cohortsComparable}
                        periodsComparable={props.periodCutoff?.aligned ?? true}
                        premiumMetricLabel={premiumMetricLabel}
                        variants={props.variants}
                        onOpenStrategy={props.onOpenStrategy}
                    />
                    <ComparisonLegend mode={props.mode} premiumMetricLabel={premiumMetricLabel} />
                </>
            )}
        </Card>
    );
};

const ComparisonLegend = ({ mode, premiumMetricLabel }: { mode: ComparisonMode; premiumMetricLabel: string }) => (
    <p className="mt-3 text-[11px] text-zinc-500">
        Each metric shows the variation&apos;s value, with the <span className="text-zinc-300">Δ</span> below it being the change vs the
        base variation — <span className="text-green-400">green</span> is better, <span className="text-red-400">red</span> is worse.{' '}
        <span className="text-zinc-300">{premiumMetricLabel}</span> is the average percentage P&amp;L per trade; for sell-to-open trades it
        uses premium as the basis. <span className="text-zinc-300">Avg ROC</span> is net P&amp;L divided by opening risk capital.{' '}
        <span className="text-zinc-300">Profit factor</span> is gross winning P&amp;L divided by gross losing P&amp;L, pooled across every
        trade (999.99 means no losing trades).
        {mode === 'best' &&
            ' Each variation uses its best combination over the full period, held fixed across all three periods. Column sorting uses overall performance.'}
        {
            ' Training and walk-forward use trade entry dates; trades crossing the cutoff stay with their entry period. Entered trades include incomplete trades. Period drawdown uses completed trades and a fresh $100,000 reference balance; period Sharpe uses realized balance changes and a zero risk-free rate.'
        }
    </p>
);

const EmptyComparison = ({ hasFamily }: { hasFamily: boolean }) => (
    <div className="rounded border border-zinc-800 bg-zinc-950 px-4 py-8 text-center text-sm text-zinc-400">
        {hasFamily ? 'This family has no variations to compare yet.' : 'Pick a strategy family to compare its variations side by side.'}
    </div>
);

interface ComparisonResultsProps {
    artifactsByStrategyId: Map<number, VariantArtifacts>;
    baseStrategyId: number | null;
    // False when variations split training/walk-forward at different cutoffs; those rows then cover different trades.
    periodsComparable: boolean;
    cohortsComparable: boolean;
    premiumMetricLabel: string;
    variants: ComparisonVariant[];
    onOpenStrategy: (strategyId: number) => void;
}

const ComparisonResults = (props: ComparisonResultsProps) => {
    const { artifactsByStrategyId, onOpenStrategy, periodsComparable, cohortsComparable, premiumMetricLabel, variants } = props;

    // Pin the base variation to the top so the default (unsorted) view reads every other row as a change relative
    // to it. Sorting any column from its header reorders freely from there.
    const orderedVariants = useMemo(() => [...variants].sort((left, right) => Number(right.isBase) - Number(left.isBase)), [variants]);

    const bestByPeriod = useMemo(() => {
        const best = (period: BacktestPeriod) =>
            computeBestMetrics(
                variants.flatMap((variant) => {
                    const metrics = variant.periods[period].metrics;
                    return metrics ? [metrics] : [];
                })
            );
        return { overall: best('overall'), training: best('training'), walkForward: best('walkForward') };
    }, [variants]);

    // Assign each variation a stable color: base is neutral gray, others cycle the palette in pinned order.
    const colorByStrategyId = useMemo(() => {
        const map = new Map<number, string>();
        let paletteIndex = 0;

        for (const variant of orderedVariants) {
            if (variant.isBase) {
                map.set(variant.strategyId, BASE_SERIES_COLOR);
            } else {
                map.set(variant.strategyId, VARIANT_SERIES_COLORS[paletteIndex % VARIANT_SERIES_COLORS.length]);
                paletteIndex += 1;
            }
        }

        return map;
    }, [orderedVariants]);

    const basePeriods = useMemo(() => variants.find((variant) => variant.isBase)?.periods ?? null, [variants]);

    const columns = useMemo<ColumnDef<ComparisonVariant>[]>(
        () => [
            {
                accessorFn: (variant) => variant.strategyId,
                cell: ({ row }) => <span className="font-mono text-xs text-zinc-400">#{row.original.strategyId}</span>,
                header: 'ID',
                id: 'strategyId',
                maxSize: 90,
                size: 70
            },
            {
                accessorFn: (variant) => variant.name,
                cell: ({ row }) => (
                    <VariationCell
                        artifacts={artifactsByStrategyId.get(row.original.strategyId)}
                        color={colorByStrategyId.get(row.original.strategyId) ?? BASE_SERIES_COLOR}
                        variant={row.original}
                        onOpenStrategy={onOpenStrategy}
                    />
                ),
                header: 'Variation',
                id: 'variation',
                size: 320
            },
            {
                accessorFn: (variant) => variant.metrics.completedBacktestCount,
                cell: ({ row }) => {
                    const currentBacktestCount = row.original.metrics.completedBacktestCount;
                    const needsRefresh = row.original.requiresBacktestRefresh;

                    return needsRefresh ? (
                        <div>
                            <div className="text-xs font-medium text-amber-300">Rerun needed</div>
                            <div className="text-[10px] text-zinc-500">{row.original.historicalBacktestCount} older</div>
                        </div>
                    ) : (
                        <span className="font-mono text-xs text-zinc-300">{currentBacktestCount}</span>
                    );
                },
                header: 'Backtests',
                id: 'backtests',
                size: 100
            },
            {
                id: 'period',
                header: 'Period / entry dates',
                enableSorting: false,
                size: 230,
                minSize: 230,
                cell: ({ row }) =>
                    row.original.periods.overall.backtestCount === 0 ? (
                        <span className="text-xs text-zinc-500">No current results</span>
                    ) : (
                        <div className="space-y-2">
                            {BACKTEST_PERIODS.map((period) => (
                                <PeriodWindowCell
                                    cutoffsDiffer={!periodsComparable}
                                    key={period}
                                    period={period}
                                    summary={row.original.periods[period]}
                                />
                            ))}
                        </div>
                    )
            },
            ...METRIC_COLUMNS.map((config): ColumnDef<ComparisonVariant> => ({
                accessorFn: (variant) => {
                    const metrics = variant.periods.overall.metrics;
                    return metrics ? (config.select(metrics) ?? undefined) : undefined;
                },
                cell: ({ row }) =>
                    row.original.periods.overall.backtestCount === 0 ? (
                        <span className="text-xs text-zinc-500">—</span>
                    ) : (
                        <div className="space-y-2">
                            {BACKTEST_PERIODS.map((period) => {
                                const summary = row.original.periods[period];
                                const metrics = summary.metrics;
                                const base = basePeriods?.[period].metrics;
                                // Incomplete cohorts, unequal populations or differing period cutoffs cannot establish winners.
                                const comparable = cohortsComparable && (period === 'overall' || periodsComparable);
                                return (
                                    <div key={period} style={{ minHeight: periodCellHeight(period, summary) }}>
                                        <span className="sr-only">{PERIOD_LABELS[period]}: </span>
                                        {metrics ? (
                                            <MetricCellContent
                                                baseValue={comparable && base ? config.select(base) : null}
                                                best={config.bestOf(bestByPeriod[period])}
                                                direction={config.direction}
                                                isBase={row.original.isBase}
                                                kind={config.kind}
                                                rankable={comparable && bestByPeriod[period].comparableCount > 1 && metrics.totalTrades > 0}
                                                secondary={config.secondary?.(metrics) ?? null}
                                                value={config.select(metrics)}
                                            />
                                        ) : (
                                            <span className="text-xs text-zinc-500">{summary.backtestCount > 0 ? 'Pending' : '—'}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ),
                header: config.header(premiumMetricLabel),
                id: config.id,
                sortUndefined: 'last'
            }))
        ],
        [
            artifactsByStrategyId,
            bestByPeriod,
            colorByStrategyId,
            basePeriods,
            onOpenStrategy,
            periodsComparable,
            cohortsComparable,
            premiumMetricLabel
        ]
    );

    return <DataTable search sort striped columns={columns} data={orderedVariants} pagination={{ enabled: false, pageSize: 20 }} />;
};

type MetricKind = 'percent' | 'ratio' | 'count';
type MetricDirection = 'higher' | 'lower' | 'neutral';

const formatMetricValue = (kind: MetricKind, value: number): string => {
    if (kind === 'percent') return `${value.toFixed(1)}%`;
    if (kind === 'ratio') return value.toFixed(2);
    return String(value);
};

const formatMetricDelta = (kind: MetricKind, delta: number): string => {
    const sign = delta > 0 ? '+' : '';
    if (kind === 'percent') return `Δ ${sign}${delta.toFixed(1)}%`;
    if (kind === 'ratio') return `Δ ${sign}${delta.toFixed(2)}`;
    return `Δ ${sign}${delta}`;
};

const isDeltaBetter = (direction: MetricDirection, delta: number): boolean =>
    (direction === 'higher' && delta > 0) || (direction === 'lower' && delta < 0);

interface MetricCellProps {
    baseValue: number | null;
    best: number | null;
    direction: MetricDirection;
    isBase: boolean;
    kind: MetricKind;
    rankable: boolean;
    secondary?: string | null;
    value: number | null;
}

// One results cell's inner content (DataTable supplies the <td>): the value, a colored delta vs. the base
// variation, a green highlight when it wins the metric, and an optional muted secondary line (e.g. total net P&L
// beneath the average-per-trade return).
const MetricCellContent = ({ baseValue, best, direction, isBase, kind, rankable, secondary, value }: MetricCellProps) => {
    if (value === null) {
        return <span className="text-zinc-600">—</span>;
    }

    const isWinner = isMetricWinner(rankable && direction !== 'neutral', best, value);
    const delta = !isBase && baseValue !== null ? value - baseValue : null;
    const hasDelta = delta !== null && Math.abs(delta) >= 1e-9;

    const deltaClass =
        direction === 'neutral' || delta === null ? 'text-zinc-500' : isDeltaBetter(direction, delta) ? 'text-green-400' : 'text-red-400';

    return (
        <div>
            <div className={isWinner ? 'font-semibold text-green-300' : 'text-zinc-100'}>{formatMetricValue(kind, value)}</div>
            {hasDelta && delta !== null && <div className={`text-[11px] ${deltaClass}`}>{formatMetricDelta(kind, delta)}</div>}
            {secondary && <div className="text-[10px] text-zinc-500">{secondary}</div>}
        </div>
    );
};

const formatCompactDollars = (value: number): string => {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
};

const ScorecardChip = ({ activeScorecard }: { activeScorecard: Scorecard | null }) => {
    if (activeScorecard) {
        return (
            <span
                className="max-w-[160px] truncate rounded bg-blue-950 px-1.5 py-0.5 text-[10px] text-blue-300"
                title={activeScorecard.name}
            >
                {activeScorecard.name}
            </span>
        );
    }

    return null;
};

// Each variation's real attached screener + scorecard, so every row reads as a concrete configuration difference.
const VariantConfigChips = ({ artifacts }: { artifacts: VariantArtifacts | undefined }) => {
    const activeScreenerRuleId = artifacts?.activeScreenerRuleId ?? null;
    const activeScorecard = artifacts?.activeScorecard ?? null;

    if (activeScreenerRuleId === null && !activeScorecard) {
        return <span className="text-[10px] text-zinc-600">No screener or scorecard</span>;
    }

    return (
        <div className="flex flex-wrap items-center gap-1">
            {activeScreenerRuleId !== null && (
                <span className="rounded bg-green-950 px-1.5 py-0.5 text-[10px] text-green-300">Screener #{activeScreenerRuleId}</span>
            )}
            <ScorecardChip activeScorecard={activeScorecard} />
        </div>
    );
};

interface MetricColumnConfig {
    id: string;
    header: (premiumMetricLabel: string) => string;
    select: (metrics: VariantMetrics) => number | null;
    direction: MetricDirection;
    kind: MetricKind;
    bestOf: (best: BestMetrics) => number | null;
    secondary?: (metrics: VariantMetrics) => string | null;
}

// The metric columns to the right of the identity/backtest columns. Each renders as a MetricCellContent carrying
// its value, delta vs. base, and winner highlight. Order mirrors the previous static table.
const METRIC_COLUMNS: MetricColumnConfig[] = [
    {
        id: 'trades',
        header: () => 'Trades',
        select: (metrics) => metrics.totalTrades,
        direction: 'neutral',
        kind: 'count',
        bestOf: () => null
    },
    {
        id: 'avgTrade',
        header: (premiumMetricLabel) => premiumMetricLabel,
        select: (metrics) => metrics.avgReturnPercent,
        direction: 'higher',
        kind: 'percent',
        bestOf: (best) => best.bestReturn,
        secondary: (metrics) => (metrics.netProfit !== null ? `${formatCompactDollars(metrics.netProfit)} net` : null)
    },
    {
        id: 'avgRoc',
        header: () => 'Avg ROC',
        select: (metrics) => metrics.avgReturnOnRiskCapitalPercent,
        direction: 'higher',
        kind: 'percent',
        bestOf: (best) => best.bestReturnOnRiskCapital,
        secondary: (metrics) => (metrics.totalRiskCapital !== null ? `${formatCompactDollars(metrics.totalRiskCapital)} risk` : null)
    },
    {
        id: 'sharpe',
        header: () => 'Sharpe',
        select: (metrics) => metrics.avgSharpeRatio,
        direction: 'higher',
        kind: 'ratio',
        bestOf: (best) => best.bestSharpe
    },
    {
        id: 'profitFactor',
        header: () => 'Profit factor',
        select: (metrics) => metrics.profitFactor,
        direction: 'higher',
        kind: 'ratio',
        bestOf: (best) => best.bestProfitFactor
    },
    {
        id: 'drawdown',
        header: () => 'Max drawdown',
        select: (metrics) => metrics.avgMaxDrawdownPercent,
        direction: 'higher',
        kind: 'percent',
        bestOf: (best) => best.bestDrawdown
    },
    {
        id: 'winRate',
        header: () => 'Win rate',
        select: (metrics) => metrics.avgWinRate,
        direction: 'higher',
        kind: 'percent',
        bestOf: (best) => best.bestWinRate
    }
];

interface VariationCellProps {
    artifacts: VariantArtifacts | undefined;
    color: string;
    variant: ComparisonVariant;
    onOpenStrategy: (strategyId: number) => void;
}

// The identity cell for a variation: color dot, name link, base badge, variant label, best combination, and the
// real attached screener/scorecard chips — so every row reads as a concrete configuration difference.
const VariationCell = ({ artifacts, color, variant, onOpenStrategy }: VariationCellProps) => (
    <div>
        <div className="flex flex-wrap items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color }} />
            <button
                className="text-left font-medium text-blue-400 underline-offset-2 hover:text-blue-300 hover:underline"
                type="button"
                onClick={() => onOpenStrategy(variant.strategyId)}
            >
                {variant.name}
            </button>
            {variant.isBase && (
                <StatusBadge status="base" tone="accent">
                    Base
                </StatusBadge>
            )}
        </div>
        {variant.variantLabel !== variant.name && <div className="mt-0.5 text-xs text-zinc-400">{variant.variantLabel}</div>}
        {variant.bestCombination && (
            <div className="mt-1 text-[11px]" title={variant.bestCombination.label}>
                <span className="text-zinc-500">Best of {variant.bestCombination.combinationCount}:</span>{' '}
                <span className="text-emerald-400/90">{variant.bestCombination.label}</span>
                <span className="text-zinc-500">
                    {' · '}
                    {variant.bestCombination.entityCount}/{variant.bestCombination.strategyEntityCount} entities
                </span>
            </div>
        )}
        <div className="mt-1.5">
            <VariantConfigChips artifacts={artifacts} />
        </div>
    </div>
);

export default StrategyVariantComparisons;

const PERIOD_LABELS: Record<BacktestPeriod, string> = { overall: 'Overall', training: 'Training', walkForward: 'Walk-forward' };
const shortDate = (date: string | null) => date?.slice(0, 10) ?? '—';

// Keep metric values aligned with the visible metadata lines in each period.
const periodCellHeight = (period: BacktestPeriod, summary: StrategyVariantPeriodSummary): number =>
    Math.max(
        56,
        16 *
            (1 +
                Number(Boolean(summary.windowStartDateTime)) +
                Number(period !== 'overall' && Boolean(summary.trainingEndDateTimeMin)) +
                Number(summary.summarizedBacktestCount < summary.backtestCount) +
                Number(summary.outOfWindowTradeCount > 0) +
                Number(summary.missingCloseTradeCount > 0) +
                Number(summary.incompleteTradeCount > 0))
    );

interface PeriodWindowCellProps {
    cutoffsDiffer: boolean;
    period: BacktestPeriod;
    summary: StrategyVariantPeriodSummary;
}

const PeriodWindowCell = ({ cutoffsDiffer, period, summary }: PeriodWindowCellProps) => {
    const cutoffNotes = [
        summary.fallbackCutoffCount > 0
            ? `${summary.fallbackCutoffCount} backtests use the agreed September 12, 2025 reporting cutoff.`
            : null,
        cutoffsDiffer ? 'Variations in this comparison use different cutoffs, so this period covers different trades across them.' : null
    ].filter((note): note is string => note !== null);

    return (
        <div className="whitespace-nowrap text-[10px] leading-4 text-zinc-500" style={{ minHeight: periodCellHeight(period, summary) }}>
            <div className="text-xs font-medium text-zinc-200">{PERIOD_LABELS[period]}</div>
            {summary.windowStartDateTime && (
                <div>
                    {shortDate(summary.windowStartDateTime)} → {shortDate(summary.windowEndDateTime)}
                </div>
            )}
            {period !== 'overall' && summary.trainingEndDateTimeMin && (
                <div
                    className={cutoffNotes.length > 0 ? 'text-amber-400' : undefined}
                    title={cutoffNotes.length > 0 ? cutoffNotes.join(' ') : undefined}
                >
                    {summary.fallbackCutoffCount > 0 ? 'Approx. cutoff ' : 'Cutoff '}
                    {shortDate(summary.trainingEndDateTimeMin)}
                    {summary.trainingEndDateTimeMin !== summary.trainingEndDateTimeMax &&
                        ` – ${shortDate(summary.trainingEndDateTimeMax)} (varies)`}
                </div>
            )}
            {summary.summarizedBacktestCount < summary.backtestCount && (
                <div className="text-amber-400">
                    {summary.summarizedBacktestCount}/{summary.backtestCount} summaries ready
                </div>
            )}
            {summary.outOfWindowTradeCount > 0 && (
                <div
                    className="text-amber-400"
                    title="Retained trades extend beyond the original recorded backtest dates; the dates above include them."
                >
                    Historical date mismatch
                </div>
            )}
            {summary.missingCloseTradeCount > 0 && <div className="text-amber-400">Incomplete closing history</div>}
            {summary.incompleteTradeCount > 0 && <div>{summary.incompleteTradeCount} incomplete trades</div>}
        </div>
    );
};
